import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Modal, ModalDescription } from '@air/primitive-modal';

import { RegenerateShareLinkButtons } from '~/components/SharePrivateContentModal/RegenerateLink/RegenerateShareLinkButtons';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRegenerateShortIdForCanvas } from '~/swr-hooks/shortUrl/useRegenerateShortIdForCanvas';

export type SharePrivateCanvasRegenerateProps = {
  canvasId: string;
  trackLocation: AirActionTrackingLocation;
  shortIdInfo: ShortUrlResponse;
  onRegenerated: () => void;
  onCancel: () => void;
};

export const SharePrivateCanvasRegenerate = memo(
  ({
    canvasId,
    trackLocation,
    shortIdInfo,
    onClose,
    onCancel,
    onRegenerated,
  }: AirModalProps<SharePrivateCanvasRegenerateProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { regenerateShortIdForCanvas } = useRegenerateShortIdForCanvas();

    const onRegenerate = useCallback(() => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      regenerateShortIdForCanvas.mutate(
        {
          workspaceId,
          canvasId,
          shortId: shortIdInfo.id,
          trackLocation,
        },
        {
          onSuccess: () => {
            onClose();
            onRegenerated();
          },
        },
      );
    }, [
      currentWorkspace?.id,
      canvasId,
      onClose,
      onRegenerated,
      regenerateShortIdForCanvas,
      shortIdInfo.id,
      trackLocation,
    ]);

    return (
      <Modal isOpen onDismiss={onClose}>
        <ShareModalHeader onClose={onClose} title="Generate new link?" />
        <ModalDescription>
          This will break the current URL and people with the old link will no longer have access to the canvas. Are you
          sure you want to generate a new URL?
        </ModalDescription>
        <RegenerateShareLinkButtons
          isLoading={regenerateShortIdForCanvas.isPending}
          onRegenerate={onRegenerate}
          onClose={() => {
            onClose();
            onCancel();
          }}
        />
      </Modal>
    );
  },
);

SharePrivateCanvasRegenerate.displayName = 'SharePrivateCanvasRegenerate';
